import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { tenantHref } from "@/lib/tenantPath";
import { useLanguage, useT } from "@/lib/LanguageContext";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
// Relative: must REALLY resolve under Jest (no "@/" alias there).
import { pluralKey } from "../lib/pluralKey";
import { GraduationCap, Clock, ArrowRight, Loader2 } from "lucide-react";

/**
 * LearnCatalogue — the public list of a tenant's published courses.
 * Route: /learn on a tenant host, /t/:tenantSlug/learn on the platform host.
 *
 * BUG-006: this page used to draw its own white bar with a GraduationCap icon and no
 * navigation or footer at all, so a visitor who reached the catalogue — from the header's
 * own "Courses" entry — had no link back into the practice's site. The chrome is now
 * resolved from `tenant.site_template` like every other interior page
 * (templates/ThemedPageLayout.jsx); the title is page content, not a header.
 */
export default function LearnCatalogue() {
  const t = useT("public");
  const ta = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/learn/courses")
      .then(({ data }) => setCourses(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <ThemedPageLayout>
      {/* pt-32 clears the site header, as on every other interior page (cf. Boutique). */}
      <section className="pt-32 pb-20 max-w-6xl mx-auto px-6" data-testid="learn-catalogue">
        <div className="max-w-3xl">
          <h1 className="font-display text-5xl md:text-6xl leading-tight">{t("learn.catalogueTitle")}</h1>
          <p className="mt-4 text-lg text-eh-muted">{t("learn.catalogueSubtitle")}</p>
        </div>

        {/* The loading state lives INSIDE the chrome: a slow answer used to blank the
            whole site, header and footer included. */}
        <div className="mt-14">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-eh-muted" />
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-24 text-eh-muted">{t("learn.catalogueEmpty")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((c) => (
                <Link
                  key={c.id}
                  to={tenantHref(`/learn/${c.id}`)}
                  data-testid={`catalogue-course-${c.id}`}
                  className="bg-white rounded-2xl border border-eh-border-soft p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col"
                >
                  {c.cover_url ? (
                    <img src={c.cover_url} alt="" className="rounded-xl mb-4 aspect-video object-cover" />
                  ) : (
                    <div className="rounded-xl mb-4 aspect-video bg-gradient-to-br from-eh-cream to-amber-50 flex items-center justify-center">
                      <GraduationCap className="w-12 h-12 text-sol-gold/50" />
                    </div>
                  )}
                  <h3 className="font-display text-xl text-eh-ink mb-2 line-clamp-2">{c.title}</h3>
                  <p className="text-sm text-eh-muted line-clamp-3 mb-4 flex-1">{c.description}</p>
                  <div className="flex items-center justify-between text-xs text-eh-muted mb-4">
                    <span className="flex items-center gap-1" data-testid={`catalogue-course-lessons-${c.id}`}><Clock className="w-3 h-3" /> {tc(pluralKey(lang, (c.lessons || []).length, "count.lessons"), { n: (c.lessons || []).length })}</span>
                    <span className="font-medium text-eh-ink text-base">
                      {c.price_cents > 0 ? `${(c.price_cents / 100).toFixed(2)} ${c.currency?.toUpperCase()}` : <span className="text-emerald-600">{ta("learn.free")}</span>}
                    </span>
                  </div>
                  {/* BUG-207 — the server now states sellability on every course row
                      (`payable_online`, `unpayable_reason`), the same law already applied
                      to programmes/products/booking services. A paid course held in a
                      currency this practice does not price in is flagged HERE, before the
                      buyer ever reaches the Enrol button on the player. */}
                  {/* A course with no lessons yet is not for sale, free or paid: no invitation
                      to open it (`unpayable_reason: "no_lessons"`, checked before currency). */}
                  {c.unpayable_reason === "no_lessons" ? (
                    <div
                      className="text-xs text-eh-muted bg-eh-cream rounded-full px-2.5 py-1 inline-block"
                      data-testid={`catalogue-course-not-available-${c.id}`}
                    >
                      {t("learn.notAvailableYet")}
                    </div>
                  ) : c.price_cents > 0 && c.payable_online === false ? (
                    <div
                      className="text-xs text-red-700 bg-red-50 rounded-full px-2.5 py-1 inline-block"
                      data-testid={`catalogue-course-not-payable-${c.id}`}
                    >
                      {ta("catalogue.currency.notPayable", { held: (c.currency || "").toUpperCase() })}
                    </div>
                  ) : (
                    <div className="text-sol-gold font-medium text-sm flex items-center gap-1">
                      {t("learn.discover")} <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </ThemedPageLayout>
  );
}
